import { get } from '../../nucleo/api';
import { mascaraCep } from '../../componentes/formulario';
import type { Fiscal, Regime, RespostaConsulta } from './tipos';

/** Situação da consulta para a tela (a chave de i18n em `cadastros:consulta.*`). */
export type EstadoConsulta = 'buscando' | 'preenchido' | 'naoEncontrado' | 'falha' | 'limite' | 'desligada' | 'cepBuscando' | 'cepPreenchido' | 'cepNaoEncontrado';

/** Endereço vindo da consulta, nos nomes de campo do formulário (`prefixo` antes de cada um: `endereco.` na criação). */
export function enderecoDaConsulta(d: Record<string, unknown> | null | undefined, prefixo = ''): Record<string, unknown> {
  if (!d) return {};
  const s = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
  return {
    [`${prefixo}cep`]: s(d.cep) ? mascaraCep(String(d.cep)) : undefined,
    [`${prefixo}logradouro`]: s(d.logradouro),
    [`${prefixo}numero`]: s(d.numero),
    [`${prefixo}complemento`]: s(d.complemento),
    [`${prefixo}bairro`]: s(d.bairro),
    [`${prefixo}cidade`]: s(d.cidade),
    [`${prefixo}uf`]: s(d.uf),
  };
}

/** Dados fiscais da Receita, no formato do `PUT /cadastros/parceiros/:id/fiscal`. */
export function fiscalDaConsulta(d: Record<string, unknown> | null | undefined): Partial<Fiscal> {
  if (!d) return {};
  const pick = <K extends keyof Fiscal>(k: K) => (d[k] === null || d[k] === undefined || d[k] === '' ? {} : { [k]: d[k] });
  return {
    ...pick('razao_social'),
    ...pick('nome_fantasia'),
    ...pick('data_abertura'),
    ...pick('porte'),
    ...pick('natureza_juridica'),
    ...pick('situacao_cadastral'),
    ...pick('situacao_data'),
    ...pick('capital_social_centavos'),
  } as Partial<Fiscal>;
}

/** Regime que a Receita informa (MEI ou Simples, com a data da opção), quando há. */
export function regimeDaConsulta(d: Record<string, unknown> | null | undefined): { regime: Regime; inicio: string } | null {
  const r = d?.regime as { regime?: Regime; inicio?: string | null } | null | undefined;
  return r?.regime && r.inicio ? { regime: r.regime, inicio: r.inicio } : null;
}

/** Estado da tela para a resposta da consulta. */
export function estadoDaResposta(r: RespostaConsulta, tipo: 'cnpj' | 'cep'): EstadoConsulta {
  if (!r.habilitada) return 'desligada';
  if (r.situacao === 'encontrado') return tipo === 'cnpj' ? 'preenchido' : 'cepPreenchido';
  if (r.situacao === 'nao_encontrado') return tipo === 'cnpj' ? 'naoEncontrado' : 'cepNaoEncontrado';
  if (r.situacao === 'limite') return 'limite';
  return 'falha';
}

/** Consulta pelo servidor (conector BrasilAPI). Erro de rede ou do servidor vira `falha`: o cadastro segue sem a consulta. */
export async function consultar(tipo: 'cnpj' | 'cep', valor: string): Promise<RespostaConsulta> {
  try {
    return await get<RespostaConsulta>(`/cadastros/consultas/${tipo}/${encodeURIComponent(valor.replace(/\D/g, ''))}`);
  } catch {
    return { habilitada: true, situacao: 'falha', dados: null, origem: null };
  }
}
